import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ArrowLeft, PlayCircle, KeyRound, Send, Loader2,
  CheckCircle2, AlertTriangle, XCircle, Copy, Radio,
} from "lucide-react";

/* WhatsApp Cloud API — Live Diagnostics.
 * Talks to /api/whatsapp/diagnostics/* using the same HttpOnly-cookie session
 * the rest of the app uses, so admins can debug Meta send failures without
 * needing raw curl or a JWT out of DevTools.
 *
 * Three actions:
 *  1. Run Diagnosis  → GET /whatsapp/diagnostics/meta
 *  2. Register       → POST /whatsapp/diagnostics/meta/register {pin}
 *  3. Test Send      → POST /whatsapp/diagnostics/meta/test-send {to,text}
 *
 * Everything Meta returns is pretty-printed verbatim so the reviewer sees
 * the exact HTTP status, error code/subcode/message, and fbtrace_id.
 */

const PILL = {
  READY:                     ["emerald", "Cloud API ready — sends should work"],
  NOT_REGISTERED:            ["amber",   "Phone not registered for Cloud API — one call away from working"],
  CODE_VERIFICATION_PENDING: ["amber",   "SMS/voice verification pending on Meta"],
  TOKEN_PHONE_MISMATCH:      ["rose",    "Token doesn't own this phone — reconnect required"],
  PHONE_NOT_ON_WABA:         ["rose",    "Phone is not on this WABA — credential mismatch"],
  WRONG_PLATFORM:            ["rose",    "Phone is on legacy On-Premise API, not Cloud API"],
  UNKNOWN:                   ["stone",   "Could not classify — inspect raw steps below"],
};

const PILL_CLASS = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  amber:   "bg-amber-50 border-amber-200 text-amber-900",
  rose:    "bg-rose-50 border-rose-200 text-rose-800",
  stone:   "bg-stone-100 border-stone-200 text-stone-800",
};

function StatusPill({ overall }) {
  const [tone, blurb] = PILL[overall] || PILL.UNKNOWN;
  const Icon = tone === "emerald" ? CheckCircle2 : tone === "amber" ? AlertTriangle : XCircle;
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium ${PILL_CLASS[tone]}`}
         data-testid="diag-overall-pill">
      <Icon className="w-4 h-4" />
      <span data-testid="diag-overall-code">{overall}</span>
      <span className="opacity-70">·</span>
      <span className="opacity-80">{blurb}</span>
    </div>
  );
}

function JsonBlock({ data, testid }) {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(data, null, 2);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <button
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] text-ink-mid hover:text-ink bg-white/80 border border-[#E7E1D3] rounded-md px-2 py-1"
        data-testid={`${testid}-copy`}>
        <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy"}
      </button>
      <pre className="text-xs bg-[#1a1a1a] text-emerald-100 rounded-2xl p-4 overflow-x-auto max-h-[420px]"
           data-testid={testid}>{text}</pre>
    </div>
  );
}

export default function WhatsAppDiagnostics() {
  const [diag, setDiag] = useState(null);
  const [regResult, setRegResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [subResult, setSubResult] = useState(null);
  const [webhookDebug, setWebhookDebug] = useState(null);
  const [traceWamid, setTraceWamid] = useState("");
  const [traceResult, setTraceResult] = useState(null);
  const [metaTelemetry, setMetaTelemetry] = useState(null);
  const [tmplName, setTmplName] = useState("appointment_reminder_2h");
  const [tmplLang, setTmplLang] = useState("en");
  const [tmplRecipient, setTmplRecipient] = useState("");
  const [loading, setLoading] = useState({ diag: false, reg: false, test: false, sub: false, wh: false, trace: false, tele: false });

  const runDiagnose = async () => {
    setLoading(l => ({ ...l, diag: true }));
    setDiag(null);
    try {
      const { data } = await api.get("/whatsapp/diagnostics/meta");
      setDiag(data);
      if (data?.overall === "READY") toast.success("Everything checks out ✓");
      else if (data?.overall === "NOT_REGISTERED") toast.info("Phone needs Cloud API registration — see Register below");
      else toast.warning(`Diagnosis: ${data?.overall}`);
    } catch (e) {
      toast.error(apiError(e, "Diagnostics call failed"));
      setDiag({ error: apiError(e), raw: e?.response?.data });
    } finally {
      setLoading(l => ({ ...l, diag: false }));
    }
  };

  const runRegister = async () => {
    const pin = window.prompt(
      "Enter a 6-digit PIN for Cloud API registration.\n\n" +
      "Meta will remember this PIN — you'll need the SAME PIN if you ever re-register.\n" +
      "If your WABA already has a 2FA PIN set, enter that existing PIN here.",
      "123456"
    );
    if (!pin) return;
    if (!/^\d{6}$/.test(pin)) { toast.error("PIN must be exactly 6 digits"); return; }
    setLoading(l => ({ ...l, reg: true }));
    setRegResult(null);
    try {
      const { data } = await api.post("/whatsapp/diagnostics/meta/register", { pin });
      setRegResult(data);
      if (data?.ok) {
        toast.success("Registered for Cloud API ✓ — re-run diagnosis to confirm READY");
      } else {
        toast.error("Registration failed — see raw response below");
      }
    } catch (e) {
      const raw = e?.response?.data;
      setRegResult({ ok: false, error: apiError(e), raw });
      toast.error(apiError(e, "Register call failed"));
    } finally {
      setLoading(l => ({ ...l, reg: false }));
    }
  };

  const runTestSend = async () => {    const to = window.prompt(
      "Send a real Meta Cloud API message to which number?\n\n" +
      "Format: international digits only, e.g. 919810453892 (no + and no spaces).\n" +
      "Use YOUR OWN registered number for the first test.",
      ""
    );
    if (!to) return;
    if (!/^\d{8,15}$/.test(to.replace(/\D/g, ""))) {
      toast.error("Phone must be 8–15 digits, no + or spaces");
      return;
    }
    setLoading(l => ({ ...l, test: true }));
    setTestResult(null);
    try {
      const { data } = await api.post("/whatsapp/diagnostics/meta/test-send", {
        to: to.replace(/\D/g, ""),
        text: "Viyora × Meta Cloud API test — you can ignore this.",
      });
      setTestResult(data);
      if (data?.ok) toast.success(`Sent ✓ wamid=${data?.response?.wamid || "—"}`);
      else toast.error(`Meta rejected: ${data?.response?.error?.message || "see raw response"}`);
    } catch (e) {
      const raw = e?.response?.data;
      setTestResult({ ok: false, error: apiError(e), raw });
      toast.error(apiError(e, "Test send failed"));
    } finally {
      setLoading(l => ({ ...l, test: false }));
    }
  };

  // Step 4 — Subscribe this WABA to our Meta App. Missing this is the classic
  // cause of Lifecycle Drawer staying stuck at "submitted": Send API returns
  // 200 + a WAMID, but Meta never dispatches the corresponding statuses[]
  // webhook because the WABA was never linked to the app via
  // POST /{WABA_ID}/subscribed_apps. Idempotent on Meta's side.
  const runSubscribeWaba = async () => {
    setLoading(l => ({ ...l, sub: true }));
    setSubResult(null);
    try {
      const { data } = await api.post("/whatsapp/subscribe-waba", {});
      setSubResult(data);
      if (data?.subscribed) {
        toast.success(`WABA subscribed to app ✓ (${data.subscribed_app_count || 1} app now linked)`);
      } else {
        toast.error("Meta rejected the subscribe call — see raw response below");
      }
    } catch (e) {
      const raw = e?.response?.data;
      setSubResult({ subscribed: false, error: apiError(e), raw });
      toast.error(apiError(e, "Subscribe call failed"));
    } finally {
      setLoading(l => ({ ...l, sub: false }));
    }
  };

  // Step 5 — Pull the full webhook pipeline report so the operator can see
  // whether Meta is delivering real status events after Step 4 succeeded.
  const runWebhookDebug = async () => {
    setLoading(l => ({ ...l, wh: true }));
    setWebhookDebug(null);
    try {
      const { data } = await api.get("/whatsapp/webhook-debug");
      setWebhookDebug(data);
      const total = data?.total_webhook_events_all_time ?? 0;
      const rej = data?.rejected_bad_signature_all_time ?? 0;
      if (total > 0 && rej === 0) toast.success(`Webhook pipeline healthy — ${total} events processed`);
      else if (rej > 0) toast.error(`${rej} webhook(s) rejected on signature — see diagnosis`);
      else toast.warning("No webhook events received yet — see diagnosis");
    } catch (e) {
      setWebhookDebug({ error: apiError(e), raw: e?.response?.data });
      toast.error(apiError(e, "webhook-debug call failed"));
    } finally {
      setLoading(l => ({ ...l, wh: false }));
    }
  };

  // Step 6 — end-to-end trace for ONE specific WAMID. This is the definitive
  // answer to "why is my message stuck at submitted": it correlates the
  // outbound row, every raw webhook Meta sent that references this WAMID,
  // and cross-checks matched vs unmatched.
  const runWamidTrace = async () => {
    const w = (traceWamid || "").trim();
    if (!w) { toast.error("Paste a WAMID first"); return; }
    setLoading(l => ({ ...l, trace: true }));
    setTraceResult(null);
    try {
      const { data } = await api.get(`/whatsapp/trace/${encodeURIComponent(w)}`);
      setTraceResult(data);
      const v = data?.verdict;
      if (v === "PIPELINE_HEALTHY" || v === "PIPELINE_HEALTHY_TERMINAL_FAILURE") toast.success(`Verdict: ${v}`);
      else if (v === "NO_META_WEBHOOK_FOR_THIS_WAMID") toast.warning(v);
      else toast.error(`Verdict: ${v}`);
    } catch (e) {
      setTraceResult({ error: apiError(e), raw: e?.response?.data });
      toast.error(apiError(e, "trace failed"));
    } finally {
      setLoading(l => ({ ...l, trace: false }));
    }
  };

  // Step 7 — Meta-side telemetry. When a WAMID exists in our DB but no
  // statuses[] webhook ever came back for it, the fault is almost always on
  // Meta's side: template quality dropped, template got paused, phone hit a
  // messaging tier limit, or the recipient isn't a WhatsApp user. This
  // endpoint queries Meta directly for all four signals in one call.
  const runMetaTelemetry = async () => {
    setLoading(l => ({ ...l, tele: true }));
    setMetaTelemetry(null);
    try {
      const params = new URLSearchParams();
      if (tmplName) params.set("template_name", tmplName);
      if (tmplLang) params.set("language", tmplLang);
      if (tmplRecipient) params.set("recipient", tmplRecipient);
      const { data } = await api.get(`/whatsapp/meta-telemetry?${params.toString()}`);
      setMetaTelemetry(data);
      toast.success("Meta telemetry fetched — see interpretation");
    } catch (e) {
      setMetaTelemetry({ error: apiError(e), raw: e?.response?.data });
      toast.error(apiError(e, "meta-telemetry failed"));
    } finally {
      setLoading(l => ({ ...l, tele: false }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="wa-diagnostics-page">
      <div>
        <Link to="/app/whatsapp/settings"
              className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink"
              data-testid="diag-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Settings
        </Link>
        <h1 className="mt-3 font-display text-3xl text-ink">Cloud API Diagnostics</h1>
        <p className="mt-1 text-sm text-ink-mid max-w-2xl">
          Talks directly to Meta's Graph API using this clinic's stored credentials.
          Use this to debug the classic <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">Unsupported request — method type: post</code> failure that appears right after Embedded Signup.
        </p>
      </div>

      {/* Step 1 — Diagnose */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 1</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Diagnose the connection</div>
            <div className="mt-1 text-sm text-ink-mid">Reads token scopes, phone platform, and WABA membership from Meta.</div>
          </div>
          <button onClick={runDiagnose} disabled={loading.diag}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="diag-run-btn">
            {loading.diag ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Run diagnosis
          </button>
        </div>
        {diag && (
          <div className="space-y-3 pt-2 border-t border-[#E7E1D3]">
            {diag.overall && <div className="pt-3"><StatusPill overall={diag.overall} /></div>}
            {diag.next_action && (
              <div className="text-sm text-ink bg-amber-50 border border-amber-200 rounded-2xl p-4"
                   data-testid="diag-next-action">
                <span className="font-medium">Next action: </span>{diag.next_action}
              </div>
            )}
            <JsonBlock data={diag} testid="diag-raw-json" />
          </div>
        )}
      </section>

      {/* Step 2 — Register */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 2 — if NOT_REGISTERED</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Register phone for Cloud API</div>
            <div className="mt-1 text-sm text-ink-mid">Calls <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">POST /{"{phone_number_id}"}/register</code> with a 6-digit PIN.</div>
          </div>
          <button onClick={runRegister} disabled={loading.reg}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#D06A42] hover:bg-[#E17550] px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="diag-register-btn">
            {loading.reg ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Register with PIN
          </button>
        </div>
        {regResult && (
          <div className="pt-2 border-t border-[#E7E1D3]">
            <JsonBlock data={regResult} testid="diag-register-json" />
          </div>
        )}
      </section>

      {/* Step 3 — Test send */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 3</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Send a raw test message</div>
            <div className="mt-1 text-sm text-ink-mid">
              Hits Meta directly, bypasses the retry wrapper, shows the full unfiltered Graph response.
            </div>
          </div>
          <button onClick={runTestSend} disabled={loading.test}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="diag-test-send-btn">
            {loading.test ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send test message
          </button>
        </div>
        {testResult && (
          <div className="pt-2 border-t border-[#E7E1D3]">
            <JsonBlock data={testResult} testid="diag-test-json" />
          </div>
        )}
      </section>

      {/* Step 4 — Repair webhook (subscribe WABA to app) */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4" data-testid="diag-subscribe-section">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 4 — if Lifecycle Drawer stays stuck at "submitted"</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Subscribe this WABA to our Meta App</div>
            <div className="mt-1 text-sm text-ink-mid max-w-2xl">
              Meta's app-level <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">messages</code> field subscription is not enough — each WABA must ALSO be linked via <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">POST /{"{WABA_ID}"}/subscribed_apps</code>.
              Missing this is the #1 reason sends succeed with a WAMID but Meta never delivers status webhooks. Idempotent — safe to click anytime.
            </div>
          </div>
          <button onClick={runSubscribeWaba} disabled={loading.sub}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50 shrink-0"
                  data-testid="diag-subscribe-btn">
            {loading.sub ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
            Subscribe WABA
          </button>
        </div>
        {subResult && (
          <div className="pt-2 border-t border-[#E7E1D3]">
            <JsonBlock data={subResult} testid="diag-subscribe-json" />
          </div>
        )}
      </section>

      {/* Step 5 — Full webhook pipeline health */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4" data-testid="diag-webhook-debug-section">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 5</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Full webhook pipeline health</div>
            <div className="mt-1 text-sm text-ink-mid max-w-2xl">
              Shows reachability, signature-guard state, callback URL match, WABA subscribed apps list, count of webhook events ever received, count rejected on signature, and last-known lifecycle status for this clinic.
            </div>
          </div>
          <button onClick={runWebhookDebug} disabled={loading.wh}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-50 shrink-0"
                  data-testid="diag-webhook-debug-btn">
            {loading.wh ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Run webhook health
          </button>
        </div>
        {webhookDebug && (
          <div className="space-y-3 pt-2 border-t border-[#E7E1D3]">
            {webhookDebug.diagnosis && (
              <div className="text-sm text-ink bg-amber-50 border border-amber-200 rounded-2xl p-4"
                   data-testid="diag-webhook-diagnosis">
                <span className="font-medium">Diagnosis: </span>{webhookDebug.diagnosis}
                {webhookDebug.recommended_action && (
                  <div className="mt-2 text-xs text-ink-mid">
                    Recommended action: <code className="text-[11px] bg-white border border-[#E7E1D3] rounded px-1 py-0.5">{webhookDebug.recommended_action}</code>
                  </div>
                )}
              </div>
            )}
            <JsonBlock data={webhookDebug} testid="diag-webhook-debug-json" />
          </div>
        )}
      </section>

      {/* Step 6 — trace ONE WAMID end-to-end */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4" data-testid="diag-trace-section">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-mid">Step 6 — the definitive "why is my message stuck?" check</div>
            <div className="mt-0.5 text-lg font-medium text-ink">Trace one WAMID end-to-end</div>
            <div className="mt-1 text-sm text-ink-mid max-w-2xl">
              Paste any WAMID that's stuck at <code className="text-xs bg-stone-100 px-1 py-0.5 rounded">submitted</code>. This correlates the outbound row, every raw Meta webhook that references it, and tells you exactly where the chain broke — or that Meta simply never sent one.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text" value={traceWamid} onChange={e => setTraceWamid(e.target.value)}
            placeholder="wamid.HBgMOTE5MzExNjkyMjE4FQIAERg…"
            className="flex-1 bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink-mid focus:outline-none focus:border-ink"
            data-testid="diag-trace-wamid-input"
            onKeyDown={e => { if (e.key === "Enter") runWamidTrace(); }}
          />
          <button onClick={runWamidTrace} disabled={loading.trace || !traceWamid.trim()}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-50 shrink-0"
                  data-testid="diag-trace-btn">
            {loading.trace ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Trace WAMID
          </button>
        </div>
        {traceResult && (
          <div className="space-y-3 pt-2 border-t border-[#E7E1D3]">
            {traceResult.verdict && (
              <div className="text-sm text-ink bg-linen border border-[#E7E1D3] rounded-2xl p-4"
                   data-testid="diag-trace-verdict">
                <div><span className="font-medium">Verdict: </span><code className="text-xs bg-white border border-[#E7E1D3] rounded px-1.5 py-0.5">{traceResult.verdict}</code></div>
                {traceResult.remediation && (
                  <div className="mt-2 text-xs text-ink-mid leading-relaxed">{traceResult.remediation}</div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-ink-mid">
                  <div>DB status: <code className="text-[11px] bg-white border border-[#E7E1D3] rounded px-1 py-0.5">{traceResult.current_db_status || "—"}</code></div>
                  <div>Webhook hits: <code className="text-[11px] bg-white border border-[#E7E1D3] rounded px-1 py-0.5">{traceResult.webhook_hit_count ?? 0}</code></div>
                </div>
              </div>
            )}
            <JsonBlock data={traceResult} testid="diag-trace-json" />
          </div>
        )}
      </section>

      {/* Step 7 — Meta-side telemetry (why did Meta not send statuses[] ?) */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4" data-testid="diag-meta-telemetry-section">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-ink-mid">Step 7 — for stuck WAMIDs where our infra is proven healthy</div>
          <div className="mt-0.5 text-lg font-medium text-ink">Ask Meta: why no statuses[] webhook?</div>
          <div className="mt-1 text-sm text-ink-mid max-w-3xl">
            Meta silently drops sends when the phone hit its messaging-tier limit, the template's quality is red or the template was paused / disabled server-side, or the recipient isn't on WhatsApp / opted out. Pulls all four signals live from Graph API.
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            value={tmplName} onChange={e => setTmplName(e.target.value)}
            placeholder="template name (e.g. appointment_reminder_2h)"
            className="bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink-mid focus:outline-none focus:border-ink"
            data-testid="diag-tele-tmpl-input"
          />
          <input
            value={tmplLang} onChange={e => setTmplLang(e.target.value)}
            placeholder="language (e.g. en)"
            className="bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink-mid focus:outline-none focus:border-ink"
            data-testid="diag-tele-lang-input"
          />
          <input
            value={tmplRecipient} onChange={e => setTmplRecipient(e.target.value)}
            placeholder="recipient (e.g. 919871383892)"
            className="bg-white border border-[#E7E1D3] rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink-mid focus:outline-none focus:border-ink"
            data-testid="diag-tele-recipient-input"
          />
        </div>
        <div>
          <button onClick={runMetaTelemetry} disabled={loading.tele}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="diag-tele-btn">
            {loading.tele ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
            Fetch Meta telemetry
          </button>
        </div>
        {metaTelemetry && (
          <div className="space-y-3 pt-2 border-t border-[#E7E1D3]">
            {Array.isArray(metaTelemetry.interpretation) && metaTelemetry.interpretation.length > 0 && (
              <div className="text-sm text-ink bg-amber-50 border border-amber-200 rounded-2xl p-4"
                   data-testid="diag-tele-interpretation">
                <div className="font-medium mb-2">Meta signal interpretation</div>
                <ul className="space-y-1">
                  {metaTelemetry.interpretation.map((v) => (
                    <li key={v} className="text-sm">{v}</li>
                  ))}
                </ul>
              </div>
            )}
            <JsonBlock data={metaTelemetry} testid="diag-tele-json" />
          </div>
        )}
      </section>
    </div>
  );
}
